<template>
  <div
    class="sidebar-form-group"
    data-test="graphics-animationSettings"
  >
    <label
      class="checkbox header-checkbox"
    >
      Animation
      <input
        v-model.lazy="animation"
        type="checkbox"
        data-test="graphics-settings-toggleAnimation"
        data-entity="graphics-settings-toggleAnimation"
        @change="onToggleAnimation"
      />
      <div class="checkmark">
        <span class="checkmark-tick fa-solid fa-check"></span>
      </div>
    </label>

    <template v-if="animation">
      <div class="sidebar-sub-group">
        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <label class="sidebar-form-label">Animate in</label>
            <select
              v-model.lazy="animationInStyle"
              data-test="graphics-settings-selectAnimationInStyle"
              data-entity="graphics-settings-selectAnimationInStyle"
            >
              <option
                v-for="option in animationInOptions"
                :key="option.value"
                :value="option.value"
                :data-test="`graphics-settings-selectAnimationInStyle-${option.value}`"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <div class="settings-row settings-row-xs">
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <validation-field as="input"
                v-model.number="animationInDuration"
                :rules="`${editingMultiple ? '' : 'required|'}decimal:0`"
                type="number"
                name="animationInDuration"
                min="0"
                :max="maxAnimationIn"
                step="250"
                :class="{ 'is-invalid': validationErrors['animationInDuration'] }"
                data-test="graphics-settings-inputAnimationInDuration"
                data-entity="graphics-settings-inputAnimationInDuration"
              />
              <span class="input-group-addon">ms</span>
            </div>
            <label class="sidebar-input-sub-label">Duration</label>
          </div>
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <validation-field as="input"
                v-model.number="animationInDelay"
                :rules="`${editingMultiple ? '' : 'required|'}decimal:0`"
                type="number"
                name="animationInDelay"
                min="0"
                :max="maxDelayIn"
                step="250"
                :class="{ 'is-invalid': validationErrors['animationInDelay'] }"
                data-test="graphics-settings-inputAnimationInDelay"
                data-entity="graphics-settings-inputAnimationInDelay"
              />
              <span class="input-group-addon">ms</span>
            </div>
            <label class="sidebar-input-sub-label">Delay</label>
          </div>
          <p
            v-if="validationErrors['animationInDuration']"
            class="text-danger"
            data-test="graphics-settings-errorAnimationInDuration"
          >
            {{ validationErrors['animationInDuration'] }}
          </p>
          <p
            v-if="validationErrors['animationInDelay']"
            class="text-danger"
            data-test="graphics-settings-errorAnimationInDelay"
          >
            {{ validationErrors['animationInDelay'] }}
          </p>
        </div>

        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <select @change="changeEasing('animationInEasing', $event)">
              <option
                v-for="f in easingFunctions"
                :key="f.name"
                :selected="f.value === animationInEasing"
                :value="f.value"
                :data-test="`graphics-settings-selectAnimationInEasing-${f.name}`"
              >
                {{ f.name }}
              </option>
              <option
                value="custom"
                :selected="inEasingName === 'custom'"
                data-test="graphics-settings-selectAnimationInEasing-custom"
              >
                Custom
              </option>
            </select>
            <label class="sidebar-input-sub-label">Easing</label>
          </div>
        </div>

        <div
          v-if="inEasingName === 'custom'"
          class="settings-row settings-row-xs"
          data-test="graphics-settings-customEasingIn"
        >
          <ceaser v-model:bezier="animationInEasing"></ceaser>
        </div>
      </div>

      <div class="sidebar-sub-group">
        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <label class="sidebar-form-label">Animate out</label>
            <select
              v-model.lazy="animationOutStyle"
              data-test="graphics-settings-selectAnimationOutStyle"
              data-entity="graphics-settings-selectAnimationOutStyle"
            >
              <option
                v-for="option in animationOutOptions"
                :key="option.value"
                :value="option.value"
                :data-test="`graphics-settings-selectAnimationOutStyle-${option.value}`"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <div class="settings-row settings-row-xs">
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <validation-field as="input"
                v-model.number.lazy="animationOutDuration"
                :rules="`${editingMultiple ? '' : 'required|'}decimal:0`"
                type="number"
                name="animationOutDuration"
                min="0"
                step="250"
                :max="maxAnimationOut"
                :class="{ 'is-invalid': validationErrors['animationOutDuration'] }"
                data-test="graphics-settings-inputAnimationOutDuration"
                data-entity="graphics-settings-inputAnimationOutDuration"
              />
              <span class="input-group-addon">ms</span>
            </div>
            <label class="sidebar-input-sub-label">Duration</label>
          </div>
          <div class="settings-col-6 settings-col-xs">
            <div class="input-group">
              <validation-field as="input"
                v-model.number.lazy="animationOutDelay"
                :rules="`${editingMultiple ? '' : 'required|'}decimal:0`"
                type="number"
                name="animationOutDelay"
                min="0"
                :max="maxDelayOut"
                step="250"
                :class="{ 'is-invalid': validationErrors['animationOutDelay'] }"
                data-test="graphics-settings-inputAnimationOutDelay"
                data-entity="graphics-settings-inputAnimationOutDelay"
              />
              <span class="input-group-addon">ms</span>
            </div>
            <label class="sidebar-input-sub-label">Delay</label>
          </div>
          <p
            v-if="validationErrors['animationOutDuration']"
            class="text-danger"
            data-test="graphics-settings-errorAnimationOutDuration"
          >
            {{ validationErrors['animationOutDuration'] }}
          </p>
          <p
            v-if="validationErrors['animationOutDelay']"
            class="text-danger"
            data-test="graphics-settings-errorAnimationOutDelay"
          >
            {{ validationErrors['animationOutDelay'] }}
          </p>
        </div>

        <div class="settings-row settings-row-xs">
          <div class="settings-col-12 settings-col-xs">
            <select
              data-test="graphics-settings-selectAnimationOutEasing"
              data-entity="graphics-settings-selectAnimationOutEasing"
              @change="changeEasing('animationOutEasing', $event)"
            >
              <option
                v-for="f in easingFunctions"
                :key="f.name"
                :selected="f.value === animationOutEasing"
                :value="f.value"
                :data-test="`graphics-settings-selectAnimationOutEasing-${f.name}`"
              >
                {{ f.name }}
              </option>
              <option
                value="custom"
                :selected="outEasingName === 'custom'"
                data-test="graphics-settings-selectAnimationOutEasing-custom"
              >
                Custom
              </option>
            </select>
            <label class="sidebar-input-sub-label">Easing</label>
          </div>
        </div>

        <div
          v-if="outEasingName === 'custom'"
          class="settings-row settings-row-xs"
          data-test="graphics-settings-customEasingOut"
        >
          <ceaser v-model:bezier="animationOutEasing"></ceaser>
        </div>
      </div>
     
      <div
        v-if="showTickerAnimation"
        class="sidebar-sub-group"
        data-test="graphics-settings-tickerAnimation"
      >
        <label class="checkbox">Ticker animation
          <input
            v-model.lazy="ticker"
            type="checkbox"
            data-test="graphics-settings-toggleTicker"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check"></span>
          </div>
        </label>

        <div class="settings-row settings-row-xs">
          <select
            v-model="tickerCrawlSpeed"
            data-test="graphics-settings-selectTickerCrawlSpeed"
            data-entity="graphics-settings-selectTickerCrawlSpeed"
          >
            <option
              value="3000"
              data-test="graphics-settings-selectTickerCrawlSpeed-slow"
            >
              Slow
            </option>
            <option
              value="2000"
              data-test="graphics-settings-selectTickerCrawlSpeed-normal"
            >
              Normal
            </option>
            <option
              value="1000"
              data-test="graphics-settings-selectTickerCrawlSpeed-fast"
            >
              Fast
            </option>
          </select>
          <label class="sidebar-input-sub-label">Speed</label>
        </div>
      </div>

      <div
        v-if="showLooping"
        class="sidebar-sub-group"
        data-test="graphics-settings-loopingAnimation"
      >
        <label class="checkbox">Looping animation
          <input
            v-model.lazy="loopingAnimation"
            type="checkbox"
            data-test="graphics-settings-toggleLooping"
            data-entity="graphics-settings-toggleLooping"
          />
          <div class="checkmark">
            <span class="checkmark-tick fa-solid fa-check"></span>
          </div>
        </label>

        <template v-if="loopingAnimation">
          <div class="settings-row settings-row-xs">
            <div class="settings-col-12 settings-col-xs">
              <select
                v-model.lazy="loopingAnimationStyle"
                data-test="graphics-settings-selectLoopingAnimationStyle"
                data-entity="graphics-settings-selectLoopingAnimationStyle"
              >
                <option
                  v-for="option in loopingAnimationOptions"
                  :key="option.value"
                  :value="option.value"
                  :data-test="`graphics-settings-selectLoopingAnimationStyle-${option.value}`"
                >
                  {{ option.label }}
                </option>
              </select>
            </div>
          </div>

          <div class="settings-row settings-row-xs">
            <div class="settings-col-6 settings-col-xs">
              <div class="input-group">
                <validation-field as="input"
                  v-model.number.lazy="loopingAnimationDuration"
                  :rules="`${editingMultiple ? '' : 'required|'}decimal:0`"
                  type="number"
                  name="loopingAnimationDuration"
                  min="0"
                  step="250"
                  :class="{
                    'is-invalid': validationErrors['loopingAnimationDuration'] ,
                  }"
                  data-test="graphics-settings-inputLoopingAnimationDuration"
                  data-entity="graphics-settings-inputLoopingAnimationDuration"
                />
                <span class="input-group-addon">ms</span>
              </div>
              <label class="sidebar-input-sub-label">Duration</label>
            </div>
            <div class="settings-col-6 settings-col-xs">
              <div class="input-group">
                <validation-field as="input"
                  v-model.number.lazy="loopingAnimationDelay"
                  :rules="`${editingMultiple ? '' : 'required|'}decimal:0`"
                  type="number"
                  name="loopingAnimationDelay"
                  min="0"
                  step="250"
                  :class="{
                    'is-invalid': validationErrors['loopingAnimationDelay'] ,
                  }"
                  data-test="graphics-settings-inputLoopingAnimationDelay"
                  data-entity="graphics-settings-inputLoopingAnimationDelay"
                />
                <span class="input-group-addon">ms</span>
              </div>
              <label class="sidebar-input-sub-label">Delay</label>
            </div>
            <p
              v-if="validationErrors['loopingAnimationDuration']"
              class="text-danger"
              data-test="graphics-settings-errorLoopingAnimationDuration"
            >
              {{ validationErrors['loopingAnimationDuration'] }}
            </p>
            <p
              v-if="validationErrors['loopingAnimationDelay']"
              class="text-danger"
              data-test="graphics-settings-errorLoopingAnimationDelay"
            >
              {{ validationErrors['loopingAnimationDelay'] }}
            </p>
          </div>

          <div class="settings-row settings-row-xs">
            <div class="settings-col-12 settings-col-xs">
              <select
                data-test="graphics-settings-selectLoopingAnimationEasing"
                data-entity="graphics-settings-selectLoopingAnimationEasing"
                @change="changeEasing('loopingAnimationEasing', $event)"
              >
                <option
                  v-for="f in easingFunctions"
                  :key="f.name"
                  :selected="f.value === loopingAnimationEasing"
                  :value="f.value"
                  :data-test="`graphics-settings-selectLoopingAnimationEasing-${f.name}`"
                >
                  {{ f.name }}
                </option>
                <option
                  value="custom"
                  :selected="loopEasingName === 'custom'"
                >
                  Custom
                </option>
              </select>
              <label class="sidebar-input-sub-label">Easing</label>
            </div>
          </div>

          <div
            v-if="loopEasingName === 'custom'"
            class="settings-row settings-row-xs"
            data-test="graphics-settings-customEasingLooping"
          >
            <ceaser v-model:bezier="loopingAnimationEasing"></ceaser>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { find } from 'lodash-es';
import SettingsMixins from '../../mixins/settings-mixins';
import CeaserComponent from '../Ceaser.vue';

export default {
  components: {
    ceaser: CeaserComponent,
  },
  mixins: [SettingsMixins],
  props: {
    element: {
      type: [String, Object],
      required: true,
    },
    showLooping: Boolean,
    default: {
      type: Boolean,
      default: true,
    },
    maxDelayIn: {
      type: Number,
      default: null,
    },
    maxDelayOut: {
      type: Number,
      default: null,
    },
    maxAnimationIn: {
      type: Number,
      default: null,
    },
    maxAnimationOut: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      easingFunctions: [
        { name: 'linear', value: '0.250, 0.250, 0.750, 0.750' },
        { name: 'ease', value: '0.250, 0.100, 0.250, 1.000' },
        { name: 'ease-in', value: '0.420, 0.000, 1.000, 1.000' },
        { name: 'ease-out', value: '0.000, 0.000, 0.580, 1.00' },
        { name: 'ease-in-out', value: '0.420, 0.000, 0.580, 1.000' },
        { name: 'easeInQuad', value: '0.550, 0.085, 0.680, 0.530' },
        { name: 'easeInCubic', value: '0.550, 0.055, 0.675, 0.190' },
        { name: 'easeInQuart', value: '0.895, 0.030, 0.685, 0.220' },
        { name: 'easeInQuint', value: '0.755, 0.050, 0.855, 0.060' },
        { name: 'easeInSine', value: '0.470, 0.000, 0.745, 0.715' },
        { name: 'easeInExpo', value: '0.950, 0.050, 0.795, 0.035' },
        { name: 'easeInCirc', value: '0.600, 0.040, 0.980, 0.335' },
        { name: 'easeInBack', value: '0.600, 0, 0.735, 0.045' },
        { name: 'easeOutQuad', value: '0.250, 0.460, 0.450, 0.940' },
        { name: 'easeOutCubic', value: '0.215, 0.610, 0.355, 1.000' },
        { name: 'easeOutQuart', value: '0.165, 0.840, 0.440, 1.000' },
        { name: 'easeOutQuint', value: '0.230, 1.000, 0.320, 1.000' },
        { name: 'easeOutSine', value: '0.390, 0.575, 0.565, 1.000' },
        { name: 'easeOutExpo', value: '0.190, 1.000, 0.220, 1.000' },
        { name: 'easeOutCirc', value: '0.075, 0.820, 0.165, 1.000' },
        { name: 'easeOutBack', value: '0.175, 0.885, 0.320, 1.275' },
        { name: 'easeInOutQuad', value: '0.455, 0.030, 0.515, 0.955' },
        { name: 'easeInOutCubic', value: '0.215, 0.610, 0.355, 1.000' },
        { name: 'easeInOutQuart', value: '0.770, 0.000, 0.175, 1.000' },
        { name: 'easeInOutQuint', value: '0.860, 0.000, 0.070, 1.000' },
        { name: 'easeInOutSine', value: '0.445, 0.050, 0.550, 0.950' },
        { name: 'easeInOutExpo', value: '1.000, 0.000, 0.000, 1.000' },
        { name: 'easeInOutCirc', value: '0.785, 0.135, 0.150, 0.860' },
        { name: 'easeInOutBack', value: '0.680, -0.550, 0.265, 1.550' },
      ],
      animationInOptions: [
        { value: 'fadeIn', label: 'Fade-in' },
        { value: 'fadeInDown', label: 'Fade-in from top' },
        { value: 'fadeInDownBig', label: 'Big fade-in from top' },
        { value: 'fadeInUp', label: 'Fade-in from bottom' },
        { value: 'fadeInUpBig', label: 'Big fade-in from bottom' },
        { value: 'fadeInLeft', label: 'Fade-in from left' },
        { value: 'fadeInLeftBig', label: 'Big fade-in from left' },
        { value: 'fadeInRight', label: 'Fade-in from right' },
        { value: 'fadeInRightBig', label: 'Big fade-in from right' },
        { value: 'slideInUp', label: 'Slide-in from bottom' },
        { value: 'slideInDown', label: 'Slide-in from top' },
        { value: 'slideInLeft', label: 'Slide-in from left' },
        { value: 'slideInRight', label: 'Slide-in from right' },
        { value: 'zoomIn', label: 'Zoom-in' },
        { value: 'flipInX', label: 'Flip-in X' },
        { value: 'flipInY', label: 'Flip-in Y' },
        { value: 'rollIn', label: 'Roll-in' },
        { value: 'popIn', label: 'Pop-in' },
        { value: 'none', label: 'None' },
      ],
      animationOutOptions: [
        { value: 'fadeOut', label: 'Fade-out' },
        { value: 'fadeOutDown', label: 'Fade-out down' },
        { value: 'fadeOutDownBig', label: 'Big fade-out down' },
        { value: 'fadeOutUp', label: 'Fade-out up' },
        { value: 'fadeOutUpBig', label: 'Big fade-out up' },
        { value: 'fadeOutLeft', label: 'Fade-out to left' },
        { value: 'fadeOutLeftBig', label: 'Big fade-out to left' },
        { value: 'fadeOutRight', label: 'Fade-out to right' },
        { value: 'fadeOutRightBig', label: 'Big fade-out to right' },
        { value: 'slideOutUp', label: 'Slide-out up' },
        { value: 'slideOutDown', label: 'Slide-out down' },
        { value: 'slideOutLeft', label: 'Slide-out to left' },
        { value: 'slideOutRight', label: 'Slide-out to right' },
        { value: 'zoomOut', label: 'Zoom-out' },
        { value: 'flipOutX', label: 'Flip-out X' },
        { value: 'flipOutY', label: 'Flip-out Y' },
        { value: 'rollOut', label: 'Roll-out' },
        { value: 'popOut', label: 'Pop-out' },
        { value: 'none', label: 'None' },
      ],
      loopingAnimationOptions: [
        { value: 'flipInOut', label: 'Flip' },
        { value: 'fadeInOut', label: 'Fade in-out' },
        { value: 'fadeInOutDown', label: 'Fade-in-out down' },
        { value: 'fadeInOutUp', label: 'Fade in-out up' },
        { value: 'fadeInOutLeft', label: 'Fade in-out to left' },
        { value: 'fadeInOutRight', label: 'Fade in-out to right' },
        { value: 'slideInOutUp', label: 'Slide in-out up' },
        { value: 'slideInOutDown', label: 'Slide in-out down' },
        { value: 'slideInOutLeft', label: 'Slide in-out to left' },
        { value: 'slideInOutRight', label: 'Slide in-out to right' },
        { value: 'zoomInOut', label: 'Zoom in-out' },
        { value: 'rollInOut', label: 'Roll in-out' },
        { value: 'popInOut', label: 'Pop in-out' },
      ],
    };
  },
  computed: {
    animation: {
      get() {
        return this.getPropertyValue('animation');
      },
      set(v) {
        this.updateOneElementValue('animation', v);
      },
    },
    animationInStyle: {
      get() {
        return this.getPropertyValue('animationInStyle');
      },
      set(v) {
        this.updateOneElementValue('animationInStyle', v);
      },
    },
    animationInDuration: {
      get() {
        return this.getPropertyValue('animationInDuration');
      },
      set(v) {
        this.updateOneElementValue('animationInDuration', v);
      },
    },
    animationInDelay: {
      get() {
        return this.getPropertyValue('animationInDelay');
      },
      set(v) {
        this.updateOneElementValue('animationInDelay', v);
      },
    },
    animationInEasing: {
      get() {
        return this.getPropertyValue('animationInEasing');
      },
      set(v) {
        this.updateOneElementValue('animationInEasing', v);
      },
    },
    animationOutStyle: {
      get() {
        return this.getPropertyValue('animationOutStyle');
      },
      set(v) {
        this.updateOneElementValue('animationOutStyle', v);
      },
    },
    animationOutDuration: {
      get() {
        return this.getPropertyValue('animationOutDuration');
      },
      set(v) {
        this.updateOneElementValue('animationOutDuration', v);
      },
    },
    animationOutDelay: {
      get() {
        return this.getPropertyValue('animationOutDelay');
      },
      set(v) {
        this.updateOneElementValue('animationOutDelay', v);
      },
    },
    animationOutEasing: {
      get() {
        return this.getPropertyValue('animationOutEasing');
      },
      set(v) {
        this.updateOneElementValue('animationOutEasing', v);
      },
    },
    ticker: {
      get() {
        return this.getPropertyValue('ticker');
      },
      set(v) {
        this.updateOneElementValue('ticker', v);
      },
    },
    tickerCrawlSpeed: {
      get() {
        return this.getPropertyValue('tickerCrawlSpeed');
      },
      set(v) {
        this.updateOneElementValue('tickerCrawlSpeed', v);
      },
    },
    loopingAnimation: {
      get() {
        return this.getPropertyValue('loopingAnimation');
      },
      set(v) {
        this.updateOneElementValue('loopingAnimation', v);
      },
    },
    loopingAnimationStyle: {
      get() {
        return this.getPropertyValue('loopingAnimationStyle');
      },
      set(v) {
        this.updateOneElementValue('loopingAnimationStyle', v);
      },
    },
    loopingAnimationDuration: {
      get() {
        return this.getPropertyValue('loopingAnimationDuration');
      },
      set(v) {
        this.updateOneElementValue('loopingAnimationDuration', v);
      },
    },
    loopingAnimationDelay: {
      get() {
        return this.getPropertyValue('loopingAnimationDelay');
      },
      set(v) {
        this.updateOneElementValue('loopingAnimationDelay', v);
      },
    },
    loopingAnimationEasing: {
      get() {
        return this.getPropertyValue('loopingAnimationEasing');
      },
      set(v) {
        this.updateOneElementValue('loopingAnimationEasing', v);
      },
    },

    inEasingName() {
      const self = this;

      const easeObj = find(this.easingFunctions, (obj) => obj.value == self.animationInEasing);

      if (easeObj) {
        return easeObj.name;
      }
      return 'custom';
    },
    outEasingName() {
      const self = this;

      const easeObj = find(this.easingFunctions, (obj) => obj.value == self.animationOutEasing);

      if (easeObj) {
        return easeObj.name;
      }
      return 'custom';
    },
    loopEasingName() {
      const self = this;

      const easeObj = find(this.easingFunctions, (obj) => obj.value == self.loopingAnimationEasing);

      if (easeObj) {
        return easeObj.name;
      }
      return 'custom';
    },
    showTickerAnimation() {
      return (
        this.getPropertyValue('type') === 'text'
        && this.getPropertyValue('ticker') === true
      );
    },
  },
  methods: {
    changeEasing(setting, $event) {
      let val = $event.target.value;
      if (val == 'custom') {
        val = '0.500, 0.250, 0.500, 0.750';
      }

      this[setting] = val;
    },

    onToggleAnimation() {
      if (!this.animation && this.ticker) {
        this.ticker = false;
      }
    },
  },
};
</script>
